/* Collective 2.0 — theme.js. Vanilla, no dependencies. Every feature degrades to plain HTML forms and links without JS. */
(() => {
  'use strict';
  const C = window.Collective || { routes: { root: '/', cartAdd: '/cart/add', cartChange: '/cart/change' }, strings: {} };
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const root = (C.routes.root || '/').replace(/\/$/, '') + '/';
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage unavailable */ } }
  };
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* Toast */
  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* Overlays. One manager owns every <dialog> (menu, search, cart, filters, localisation, lightbox).
     Rules: only one overlay is open at a time; opening one closes the others and any mega menu;
     scroll is locked while any overlay is open and released when the last one closes; focus returns
     to the control that opened it; Escape, the backdrop and [data-close] all close through the same path. */
  const Overlay = (() => {
    const html = document.documentElement;
    let current = null;
    const openers = (id) => $$(`[aria-controls="${id}"]`);
    const lock = () => {
      if (html.classList.contains('is-locked')) return;
      const gap = window.innerWidth - html.clientWidth;
      html.style.setProperty('--scrollbar-gap', `${Math.max(gap, 0)}px`);
      html.classList.add('is-locked');
    };
    const unlock = () => { html.classList.remove('is-locked'); html.style.removeProperty('--scrollbar-gap'); };
    const close = (d, { restoreFocus = true } = {}) => {
      if (!d || !d.open) return;
      d._restoreFocus = restoreFocus;
      d.close();
    };
    const closeAll = (opts) => { $$('dialog[open]').forEach((d) => close(d, opts)); };
    const onClose = (d) => {
      openers(d.id).forEach((o) => o.setAttribute('aria-expanded', 'false'));
      if (current === d) current = null;
      if (!document.querySelector('dialog[open]')) unlock();
      const back = d._opener;
      d._opener = null;
      if (d._restoreFocus !== false && back && document.contains(back) && back.offsetParent !== null) back.focus({ preventScroll: true });
      d.dispatchEvent(new CustomEvent('overlay:closed', { bubbles: true }));
    };
    const open = (id, opener) => {
      const d = document.getElementById(id);
      if (!d || typeof d.showModal !== 'function') return false;
      if (d.open) return true;
      document.dispatchEvent(new CustomEvent('overlay:before-open', { detail: { id } }));
      closeAll({ restoreFocus: false });
      d._opener = opener || document.activeElement;
      if (!d._wired) {
        d._wired = true;
        d.addEventListener('close', () => onClose(d));
        d.addEventListener('cancel', (e) => { e.preventDefault(); close(d); });
      }
      d.showModal();
      current = d;
      lock();
      openers(id).forEach((o) => o.setAttribute('aria-expanded', 'true'));
      const focusTarget = d.querySelector('[autofocus]') || d.querySelector('[data-close]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      d.dispatchEvent(new CustomEvent('overlay:opened', { bubbles: true }));
      return true;
    };
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (opener) {
        if (open(opener.dataset.open, opener)) e.preventDefault();
        return;
      }
      const closer = e.target.closest('[data-close]');
      if (closer && closer.closest('dialog')) { e.preventDefault(); close(closer.closest('dialog')); return; }
      // Click on the ::backdrop lands on the dialog element itself, outside its box.
      if (e.target instanceof HTMLDialogElement && e.target.open) {
        const r = e.target.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) close(e.target);
      }
    });
    // Escape always closes the open overlay, even when focus is in a search field (which would otherwise
    // swallow the first Escape to clear its text).
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !current || !current.open) return;
      e.preventDefault();
      close(current);
    }, true);
    // Drawers that only exist for small screens are closed when the layout changes underneath them.
    const desktop = window.matchMedia('(min-width: 990px)');
    const onBreakpoint = () => {
      $$('dialog[open][data-mobile-only]').forEach((d) => { if (desktop.matches) close(d, { restoreFocus: false }); });
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    // Back/forward cache: never resume with a stale overlay or a locked page.
    window.addEventListener('pageshow', (e) => { if (e.persisted) { closeAll({ restoreFocus: false }); unlock(); } });
    return { open, close, closeAll, get current() { return current; } };
  })();
  const openDialog = (id, opener) => Overlay.open(id, opener);
  window.Collective = window.Collective || C;
  window.Collective.overlay = Overlay;

  /* Header. Shopify wraps the header section in <div class="shopify-section ... section-header">, so the
     sticky element must be that wrapper (a sticky child of a same-height wrapper never sticks). */
  const headerInner = $('[data-header]');
  const headerWrap = headerInner ? (headerInner.closest('.shopify-section') || headerInner) : null;
  if (headerWrap) {
    const behaviour = headerInner.dataset.sticky || 'scroll-up';
    headerWrap.classList.add('header-sticky', `header-sticky--${behaviour}`);
    let lastY = window.scrollY;
    const html = document.documentElement;
    const setOffset = () => {
      const r = headerWrap.getBoundingClientRect();
      const visible = behaviour !== 'none' && !headerWrap.classList.contains('is-hidden');
      html.style.setProperty('--header-offset', visible ? `${Math.max(Math.round(r.bottom), 0)}px` : '0px');
      html.style.setProperty('--header-bottom', `${Math.max(Math.round(r.bottom), 0)}px`);
    };
    const megaOpen = () => !!headerWrap.querySelector('.mega.is-open');
    const onScroll = () => {
      const y = Math.max(window.scrollY, 0);
      headerInner.querySelector('.site-header')?.classList.toggle('is-scrolled', y > 10);
      if (behaviour === 'scroll-up' && !megaOpen() && !headerWrap.contains(document.activeElement) && !html.classList.contains('is-locked')) {
        const down = y > lastY + 4; const up = y < lastY - 4;
        if (down && y > 160) headerWrap.classList.add('is-hidden');
        else if (up || y <= 160) headerWrap.classList.remove('is-hidden');
      }
      lastY = y;
      setOffset();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(setOffset, 100));
    headerWrap.addEventListener('transitionend', setOffset);
    setOffset();

    const backdrop = $('[data-header-backdrop]');
    const closeAll = () => {
      $$('[data-mega-toggle]', headerWrap).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      $$('.mega', headerWrap).forEach((m) => m.classList.remove('is-open'));
      if (backdrop) backdrop.classList.remove('is-visible');
    };
    const openMega = (btn) => {
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel || Overlay.current) return;
      closeAll();
      headerWrap.classList.remove('is-hidden');
      btn.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
      setOffset();
    };
    document.addEventListener('overlay:before-open', () => { clearTimeout(hoverTimer); closeAll(); });
    /* Mega menu input model:
       - Mouse: hover intent opens a panel; it stays open while the pointer is anywhere over the header bar or
         the panel and closes 250ms after leaving both. One shared timer, so no two handlers can fight.
       - Each top-level item is a link: click or Enter goes to its collection. Touch: first tap opens, second follows.
       - Keyboard: ArrowDown or Space opens the panel and moves focus into it.
       - Escape closes and returns focus; focus leaving the header closes. */
    let hoverTimer;
    let openedBy = null; // 'hover' | 'mouse' | 'touch' | 'key'
    let lastPointer = 'mouse';
    const isOpen = (btn) => btn.getAttribute('aria-expanded') === 'true';
    document.addEventListener('pointerdown', (e) => { lastPointer = e.pointerType || 'mouse'; }, true);
    $$('[data-mega-toggle]', headerWrap).forEach((btn) => {
      // Top-level items are real links to their collection. Mouse and keyboard Enter follow the link;
      // on touch (no hover) the first tap opens the panel and a second tap on the open item follows the link.
      btn.addEventListener('click', (e) => {
        clearTimeout(hoverTimer);
        const source = e.detail === 0 ? 'key' : (lastPointer === 'mouse' ? 'mouse' : 'touch');
        if (source !== 'touch' || isOpen(btn)) return;
        e.preventDefault();
        openMega(btn); openedBy = 'touch';
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown' && e.key !== ' ') return;
        e.preventDefault();
        if (!isOpen(btn)) { openMega(btn); openedBy = 'key'; }
        document.getElementById(btn.getAttribute('aria-controls'))?.querySelector('a[href]')?.focus();
      });
      const item = btn.closest('.nav__item');
      if (!item) return;
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        if (isOpen(btn)) return;
        // Opening from closed: short intent delay. Switching from another open panel: longer, so a diagonal
        // path that brushes a neighbouring trigger on its way down into the panel does not switch menus.
        hoverTimer = setTimeout(() => { openMega(btn); openedBy = 'hover'; }, megaOpen() ? 160 : 100);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        if (!isOpen(btn)) clearTimeout(hoverTimer); // cancel a pending open on a trigger merely brushed past
      });
    });
    // The hover zone for an open panel is the whole header bar plus the panel (both inside .site-header-wrapper),
    // not just the narrow trigger, so moving diagonally from a trigger to any link never crosses a "dead" area.
    headerInner.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse' && openedBy === 'hover') clearTimeout(hoverTimer);
    });
    headerInner.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(hoverTimer);
      if (!megaOpen() || openedBy === 'key' || openedBy === 'touch') return;
      hoverTimer = setTimeout(() => { closeAll(); openedBy = null; }, 250);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && megaOpen()) { const b = headerWrap.querySelector('[data-mega-toggle][aria-expanded=true]'); clearTimeout(hoverTimer); closeAll(); if (b) b.focus(); }
    });
    if (backdrop) backdrop.addEventListener('click', () => { clearTimeout(hoverTimer); closeAll(); });
    headerWrap.addEventListener('focusout', (e) => { if (!headerWrap.contains(e.relatedTarget)) closeAll(); });
    window.matchMedia('(min-width: 990px)').addEventListener?.('change', closeAll);
  }

  /* Hero video: pause control (WCAG 2.2.2), honour reduced motion, play only the visible video */
  $$('.hero').forEach((hero) => {
    const btn = $('[data-hero-pause]', hero);
    const vids = $$('[data-hero-video] video', hero);
    if (!vids.length) return;
    const setState = (paused) => {
      vids.forEach((v) => { if (paused || v.offsetParent === null) v.pause(); else v.play().catch(() => {}); });
      if (btn) {
        btn.setAttribute('aria-pressed', String(paused));
        btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
        const p = btn.querySelector('[data-icon-pause]'); const pl = btn.querySelector('[data-icon-play]');
        if (p && pl) { p.style.display = paused ? 'none' : ''; pl.style.display = paused ? '' : 'none'; }
      }
    };
    let paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setState(paused);
    if (btn) btn.addEventListener('click', () => { paused = !paused; setState(paused); });
    window.addEventListener('resize', debounce(() => setState(paused), 200));
  });

  /* Predictive search */
  const searchDialog = $('#SearchDialog');
  if (searchDialog) {
    const input = $('input[name="q"]', searchDialog);
    const results = $('[data-predictive]', searchDialog);
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const url = `${C.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,article,page&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parse(await res.text());
        const content = $('[data-predictive-content]', doc);
        results.innerHTML = content ? content.innerHTML : '';
      } catch (err) { if (err.name !== 'AbortError') results.innerHTML = ''; }
    }, 220);
    input.addEventListener('input', run);
  }

  /* Cart */
  const Cart = {
    sectionsFor() {
      const ids = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },
    async refresh(sections) {
      const ids = sections || this.sectionsFor();
      try {
        const res = await fetch(`${root}?sections=${ids.join(',')}`);
        const data = await res.json();
        this.render(data);
      } catch (e) { /* keep current UI */ }
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parse(html);
        if (id === 'cart-drawer') {
          const next = $('[data-cart-drawer-inner]', doc);
          const cur = $('[data-cart-drawer-inner]');
          if (next && cur) cur.innerHTML = next.innerHTML;
        } else {
          const next = $(`[data-cart-page]`, doc);
          const cur = $(`[data-cart-page]`);
          if (next && cur) cur.innerHTML = next.innerHTML;
        }
        const count = $('[data-cart-count-source]', doc);
        if (count) $$('[data-cart-count]').forEach((el) => { el.textContent = count.dataset.cartCountSource; });
      });
    },
    async add(form) {
      const btn = form.querySelector('[type="submit"][name="add"]');
      const fd = new FormData(form);
      fd.append('sections', this.sectionsFor().join(','));
      fd.append('sections_url', window.location.pathname);
      if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
      try {
        const res = await fetch(`${C.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
        const data = await res.json();
        if (!res.ok || data.status) { this.error(form, data.description || data.message || C.strings.error); return; }
        this.render(data.sections);
        const msg = form.querySelector('[data-form-error]');
        if (msg) msg.hidden = true;
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        if (C.cartType === 'drawer' && document.getElementById('CartDrawer')) openDialog('CartDrawer', btn);
        else toast(C.strings.added);
      } catch (e) {
        form.dataset.ajaxCart = 'off';
        form.submit();
      } finally {
        if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = btn.dataset.unavailable === 'true'; }
      }
    },
    error(form, message) {
      const box = form.querySelector('[data-form-error]');
      if (box) { box.textContent = message; box.hidden = false; } else toast(message);
    },
    async change(line, quantity, row) {
      if (row) row.classList.add('is-loading');
      try {
        const res = await fetch(`${C.routes.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionsFor(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) { toast(data.description || C.strings.error); await this.refresh(); return; }
        this.render(data.sections);
      } catch (e) { toast(C.strings.error); }
      finally { if (row) row.classList.remove('is-loading'); }
    }
  };
  window.Collective.cart = Cart;

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-ajax-cart]') || form.dataset.ajaxCart === 'off') return;
    if (C.cartType === 'page') return;
    const submitter = e.submitter;
    if (submitter && submitter.name !== 'add') return;
    e.preventDefault();
    Cart.add(form);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (btn) {
      const wrap = btn.closest('.qty');
      const input = wrap && wrap.querySelector('input');
      if (!input) return;
      const step = Number(btn.dataset.qtyStep);
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value || 0) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) { e.preventDefault(); Cart.change(Number(remove.dataset.cartRemove), 0, remove.closest('.cart-item')); }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (input) Cart.change(Number(input.dataset.cartQty), Math.max(0, Number(input.value)), input.closest('.cart-item'));
  });

  /* Product form: variants, availability, gallery, sticky mobile bar */
  const initProduct = (section) => {
    const dataEl = $('[data-product-json]', section);
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const form = $('[data-product-form]', section);
    const idInput = form && form.querySelector('input[name="id"]');
    const pickers = $$('[data-option-index]', section);
    const addBtn = form && form.querySelector('[name="add"]');
    const labels = data.labels || {};

    const currentOptions = () => pickers.map((fs) => {
      const checked = fs.querySelector('input:checked');
      const select = fs.querySelector('select');
      return checked ? checked.value : (select ? select.value : null);
    });
    const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => opts[i] === undefined || opts[i] === null || o === opts[i]));

    const markUnavailable = (opts) => {
      pickers.forEach((fs, idx) => {
        $$('input', fs).forEach((inp) => {
          const probe = opts.slice(); probe[idx] = inp.value;
          const match = data.variants.find((v) => v.options.every((o, i) => o === probe[i]));
          inp.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const update = (variant) => {
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; addBtn.dataset.unavailable = 'true'; addBtn.querySelector('span').textContent = C.strings.unavailable; }
        return;
      }
      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      const state = data.states[variant.id] || (variant.available ? 'available' : 'sold_out');
      $$('[data-variant-price]', section).forEach((el) => { el.innerHTML = data.prices[variant.id] || el.innerHTML; });
      const avail = $('[data-availability]', section);
      if (avail) {
        avail.className = `availability availability--${state}`;
        const text = $('[data-availability-text]', avail);
        if (text) text.textContent = labels[state] || '';
        const note = $('[data-availability-note]', section);
        if (note) { note.textContent = (data.notes && data.notes[state]) || ''; note.hidden = !note.textContent; }
      }
      const badgeSlot = $('[data-gallery-badge]', section);
      if (badgeSlot) badgeSlot.innerHTML = data.badges[state] || '';
      if (addBtn) {
        const buyable = state !== 'sold_out' && state !== 'coming_soon' && variant.available;
        addBtn.disabled = !buyable;
        addBtn.dataset.unavailable = buyable ? 'false' : 'true';
        const span = addBtn.querySelector('span');
        if (span) span.textContent = buyable ? (state === 'pre_order' ? labels.pre_order_cta : labels.add) : labels.sold_out_cta;
      }
      $$('[data-sku]', section).forEach((el) => { el.textContent = variant.sku || ''; el.closest('[data-sku-wrap]')?.toggleAttribute('hidden', !variant.sku); });
      $$('[data-selected-value]', section).forEach((el) => { const idx = Number(el.dataset.selectedValue); el.textContent = variant.options[idx] || ''; });
      if (variant.featured_media_id) {
        const target = $(`[data-media-id="${variant.featured_media_id}"]`, section);
        const list = $('.gallery__list', section);
        if (target && list) {
          if (window.matchMedia('(min-width: 990px)').matches) { if (target !== list.firstElementChild) list.prepend(target); }
          else list.scrollTo({ left: target.offsetLeft - list.offsetLeft, behavior: 'smooth' });
        }
      }
      markUnavailable(variant.options);
      const mb = $('[data-mobile-buy-price]');
      if (mb) mb.innerHTML = data.prices[variant.id] || '';
      document.dispatchEvent(new CustomEvent('variantChange', { detail: { variant, productId: data.id, sectionId: section.id } }));
      if (window._RestockRocket && typeof window._RestockRocket.renderButtonForVariant === 'function') {
        try { window._RestockRocket.renderButtonForVariant(variant.id); } catch (e) { /* STOQ manages its own UI */ }
      }
    };

    pickers.forEach((fs) => fs.addEventListener('change', () => update(findVariant(currentOptions()))));
    if (pickers.length) markUnavailable(currentOptions());

    const gallery = $('.gallery__list', section);
    const dots = $$('.gallery__dots button', section);
    if (gallery && dots.length) {
      gallery.addEventListener('scroll', debounce(() => {
        const i = Math.round(gallery.scrollLeft / gallery.clientWidth);
        dots.forEach((d, n) => d.setAttribute('aria-current', n === Math.abs(i) ? 'true' : 'false'));
      }, 60), { passive: true });
      dots.forEach((d, n) => d.addEventListener('click', () => gallery.scrollTo({ left: gallery.clientWidth * n * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' })));
    }
    $$('[data-lightbox-open]', section).forEach((el) => el.addEventListener('click', (e) => {
      e.preventDefault();
      const box = $('[data-lightbox]', section);
      if (!box) return;
      openDialog(box.id, el);
      const img = box.querySelector(`[data-lightbox-item="${el.dataset.lightboxOpen}"]`);
      if (img) img.scrollIntoView({ block: 'start' });
    }));

    const mobileBar = $('[data-mobile-buy]');
    if (mobileBar && addBtn) {
      // Position check on scroll rather than IntersectionObserver alone: a fast flick can jump from
      // "below the fold" to "above the fold" without an intersection change ever being reported.
      let ticking = false;
      const sync = () => {
        ticking = false;
        const r = addBtn.getBoundingClientRect();
        const past = r.bottom < 0 && !document.documentElement.classList.contains('is-locked');
        if (mobileBar.classList.contains('is-visible') === past) return;
        mobileBar.classList.toggle('is-visible', past);
        mobileBar.inert = !past;
      };
      mobileBar.inert = true;
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(sync); } }, { passive: true });
      document.addEventListener('overlay:opened', sync);
      document.addEventListener('overlay:closed', sync);
      sync();
      const mbBtn = mobileBar.querySelector('button');
      if (mbBtn) mbBtn.addEventListener('click', () => { addBtn.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => addBtn.focus({ preventScroll: true }), 450); });
    }
  };
  $$('[data-product-section]').forEach(initProduct);

  /* Recommendations (related and complementary) */
  $$('[data-recommendations]').forEach(async (el) => {
    try {
      const res = await fetch(el.dataset.url);
      const doc = parse(await res.text());
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.querySelector('[data-has-items]')) el.innerHTML = fresh.innerHTML;
      else el.closest('[data-recommendations-wrap]')?.setAttribute('hidden', '');
    } catch (e) { el.closest('[data-recommendations-wrap]')?.setAttribute('hidden', ''); }
  });

  /* Collection facets, sort, density, load more */
  const facetRoot = $('[data-facets-root]');
  if (facetRoot) {
    const sectionId = facetRoot.dataset.sectionId;
    const render = async (search, push = true) => {
      const grid = $('[data-collection-grid]');
      if (grid) grid.classList.add('is-loading');
      const url = `${window.location.pathname}?${search}`;
      try {
        const res = await fetch(`${window.location.pathname}?section_id=${sectionId}&${search}`);
        const doc = parse(await res.text());
        ['[data-collection-grid]', '[data-facets-form-wrap]', '[data-active-filters]', '[data-product-count]', '[data-pagination]', '[data-facets-drawer-body]'].forEach((sel) => {
          const next = $(sel, doc); const cur = $(sel);
          if (next && cur) cur.innerHTML = next.innerHTML;
        });
        if (push) window.history.pushState({ search }, '', url);
        applyDensity();
      } catch (e) { window.location.href = url; }
      finally { const g = $('[data-collection-grid]'); if (g) g.classList.remove('is-loading'); }
    };
    const fromForm = (form) => {
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      const sort = $('[data-sort]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      return params.toString();
    };
    document.addEventListener('change', (e) => {
      const form = e.target.closest('[data-facets-form]');
      if (form) render(fromForm(form));
      if (e.target.matches('[data-sort]')) {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', e.target.value); params.delete('page');
        render(params.toString());
      }
    });
    document.addEventListener('submit', (e) => { const form = e.target.closest('[data-facets-form]'); if (form) { e.preventDefault(); render(fromForm(form)); } });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-facet-remove]');
      if (a) { e.preventDefault(); const u = new URL(a.href, window.location.origin); render(u.searchParams.toString()); }
    });
    window.addEventListener('popstate', (e) => render((e.state && e.state.search) || window.location.search.slice(1), false));

    const applyDensity = () => {
      const grid = $('[data-collection-grid] .grid');
      if (!grid) return;
      const d = store.get('collective:density:desktop');
      const m = store.get('collective:density:mobile');
      if (d) grid.style.setProperty('--cols-desktop', d);
      if (m) grid.style.setProperty('--cols-mobile', m);
      $$('[data-density]').forEach((b) => {
        const v = b.dataset.density; const scope = b.dataset.scope;
        const cur = scope === 'mobile' ? (m || grid.style.getPropertyValue('--cols-mobile') || b.closest('[data-default]')?.dataset.default) : (d || b.closest('[data-default]')?.dataset.default);
        b.setAttribute('aria-pressed', String(String(cur).trim() === v));
      });
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-density]');
      if (!b) return;
      store.set(`collective:density:${b.dataset.scope}`, b.dataset.density);
      applyDensity();
    });
    applyDensity();

    document.addEventListener('click', async (e) => {
      const more = e.target.closest('[data-load-more]');
      if (!more) return;
      e.preventDefault();
      more.setAttribute('aria-busy', 'true');
      try {
        const u = new URL(more.href, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        const doc = parse(await (await fetch(u)).text());
        const items = $$('[data-collection-grid] .grid > *', doc);
        const grid = $('[data-collection-grid] .grid');
        items.forEach((it) => grid.appendChild(it));
        const nextPag = $('[data-pagination]', doc);
        const curPag = $('[data-pagination]');
        if (nextPag && curPag) curPag.innerHTML = nextPag.innerHTML;
        const firstNew = items[0] && items[0].querySelector('a');
        if (firstNew) firstNew.focus({ preventScroll: true });
      } catch (err) { window.location.href = more.href; }
    });
  }

  /* Dealer locator */
  const locator = $('[data-locator]');
  if (locator) {
    let dealers = [];
    try { dealers = JSON.parse($('[data-dealers-json]', locator).textContent).map((d) => ({ ...d, lat: d.lat === null ? null : parseFloat(d.lat), lng: d.lng === null ? null : parseFloat(d.lng) })); } catch (e) { dealers = []; }
    const items = $$('[data-dealer]', locator);
    const search = $('[data-locator-search]', locator);
    const status = $('[data-locator-status]', locator);
    const mapEl = $('[data-locator-map]', locator);
    let region = 'all';
    let map; let markers = {};
    const normalise = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const filter = () => {
      const q = normalise(search && search.value);
      let shown = 0;
      items.forEach((li) => {
        const okRegion = region === 'all' || li.dataset.region === region;
        const okText = !q || normalise(li.dataset.search).includes(q);
        const show = okRegion && okText;
        li.hidden = !show;
        if (show) shown += 1;
        const m = markers[li.dataset.dealer];
        if (m && map) { if (show) m.addTo(map); else m.remove(); }
      });
      if (status) status.textContent = (status.dataset.template || '[count]').replace('[count]', shown);
      if (map) {
        const visible = items.filter((li) => !li.hidden).map((li) => markers[li.dataset.dealer]).filter(Boolean);
        if (visible.length) map.fitBounds(window.L.featureGroup(visible).getBounds().pad(0.2), { maxZoom: 12 });
      }
    };
    if (search) search.addEventListener('input', debounce(filter, 150));
    $$('[data-region]', locator).forEach((b) => b.addEventListener('click', () => {
      region = b.dataset.region;
      $$('[data-region]', locator).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      filter();
    }));
    const focusDealer = (id) => {
      items.forEach((li) => li.classList.toggle('is-active', li.dataset.dealer === id));
      const m = markers[id];
      if (m && map) { map.setView(m.getLatLng(), 13); m.openPopup(); }
    };
    $$('[data-dealer-focus]', locator).forEach((b) => b.addEventListener('click', () => focusDealer(b.dataset.dealerFocus)));

    const loadMap = () => new Promise((resolve, reject) => {
      if (window.L) { resolve(); return; }
      const css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = mapEl.dataset.leafletCss; document.head.appendChild(css);
      const s = document.createElement('script');
      s.src = mapEl.dataset.leafletJs; s.onload = resolve; s.onerror = reject; document.head.appendChild(s);
      setTimeout(() => reject(new Error('timeout')), 10000);
    });
    const initMap = async () => {
      try {
        await loadMap();
        const L = window.L;
        mapEl.innerHTML = '';
        map = L.map(mapEl, { scrollWheelZoom: false, worldCopyJump: true }).setView([30, 0], 2);
        L.tileLayer(mapEl.dataset.tiles, { maxZoom: 18, attribution: mapEl.dataset.attribution }).addTo(map);
        const icon = L.divIcon({ className: 'locator-pin', html: '<span style="display:block;width:14px;height:14px;background:#000;border:2px solid #c7ff4a;border-radius:50%"></span>', iconSize: [14, 14] });
        dealers.forEach((d) => {
          if (!Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return;
          const m = L.marker([d.lat, d.lng], { icon, title: d.name, keyboard: true });
          const wrap = document.createElement('div');
          const strong = document.createElement('strong'); strong.textContent = d.name; wrap.appendChild(strong);
          const p = document.createElement('div'); p.textContent = d.address; wrap.appendChild(p);
          m.bindPopup(wrap);
          m.on('click', () => { items.forEach((li) => li.classList.toggle('is-active', li.dataset.dealer === d.id)); const li = items.find((x) => x.dataset.dealer === d.id); if (li) li.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); });
          markers[d.id] = m;
        });
        filter();
      } catch (e) {
        mapEl.innerHTML = `<div class="locator__map-fallback">${mapEl.dataset.fallback}</div>`;
      }
    };
    if (mapEl) {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { io.disconnect(); initMap(); } }, { rootMargin: '200px' });
        io.observe(mapEl);
      } else initMap();
    }
    const near = $('[data-locator-near]', locator);
    if (near && 'geolocation' in navigator) {
      near.hidden = false;
      near.addEventListener('click', () => navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        const dist = (d) => (Number.isFinite(d.lat) ? Math.hypot(d.lat - latitude, (d.lng - longitude) * Math.cos(latitude * Math.PI / 180)) : 1e9);
        const order = dealers.slice().sort((a, b) => dist(a) - dist(b)).map((d) => d.id);
        const list = $('[data-locator-list]', locator);
        order.forEach((id) => { const li = items.find((x) => x.dataset.dealer === id); if (li) list.appendChild(li); });
        if (map) map.setView([latitude, longitude], 8);
      }, () => toast(near.dataset.error)));
    }
    filter();
  }

})();
